import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import useHandleAppException, {
  AppExceptionResponse,
} from '../../../../../../../../../hooks/useHandleAppException';
import LangEnum from '../../../../../../../../../lang/LangEnum';
import { restApi } from '../../../../../../../../../services/api';
import snackbarStore from '../../../../../../../../../stores/Snackbar.store';
import { formatDateFromApi, formatDateToApi } from '../../../../../../../../../utils/dates';
import { useApplicationContext } from '../../../../../../../../context/ApplicationContext/ApplicationContext';
import { useLoadingStatusStore } from '../../../../../../../../context/loadingStatus/LoadingStatus.store';
import { UsedInOfflineMode } from '../../../../../../../../offline/types/UsedInOfflineMode';
import { useTranslation } from '../../../../../../../../translation/TranslationContext';
import { GetOrderConditionDTO } from '../../../../../../../conditions/dtos/orderCondition.dto';
import { OrderCondition } from '../../../../../../../conditions/interfaces/orderCondition.interface';
import { orderConditionRepository } from '../../../../../../../conditions/orderCondition.repository';
import { OrderConditionSubmitData } from '../../../../../../../conditions/orderCondition.types';
import { useOrderCalculateContext } from '../../../../../../../contexts/OrderCalculate.context';
import { useOrderConditionContext } from '../../../../../../../contexts/OrderCondition.context';
import { validateOrderConditionsQuery } from '../../../../../../../contexts/order.utils';
import { Order } from '../../../../../../../order.types';
import useOrderConditionFieldsVisibility from '../../../hooks/useOrderConditionFieldsVisibility';
import { OrderConditionApplyToAllState } from '../../OrderConditionApplyToAllModal';
import { isQuotedFreight } from '../../OrderConditionFreightField/orderConditionFreightField.utils';
import {
  MIN_DELIVERY_TERM_BUSINESS_DAYS,
  OrderConditionDetailsFormValues,
  buildOrderConditionDetailsSchema,
} from '../orderConditionDetailsForm.schema';
import {
  SERVER_FREIGHT_FIELDS,
  getComparableValue,
  shouldSyncFreightFromServer,
} from '../orderConditionDetailsForm.utils';

interface UseOrderConditionDetailsFormParams extends UsedInOfflineMode {
  order: Order;
  condition: OrderCondition;
}

interface PropagableChange {
  field: PropagableField;
  value: OrderConditionDetailsFormValues[PropagableField];
}

interface UseOrderConditionDetailsFormResult {
  formMethods: UseFormReturn<OrderConditionDetailsFormValues>;
  save: () => Promise<void>;
  applyToAll: OrderConditionApplyToAllState;
}

const PROPAGABLE_FIELDS = [
  'paymentId',
  'commercialPolicyId',
  'deliveryAddressId',
  'shippingCostValue',
  'internalObservation',
  'observation',
] as const;

type PropagableField = (typeof PROPAGABLE_FIELDS)[number];

const isQuotedFreightChange = (field: PropagableField, values: OrderConditionDetailsFormValues) =>
  field === 'shippingCostValue' && isQuotedFreight(values);

const getChangedFields = (
  values: OrderConditionDetailsFormValues,
  savedValues: OrderConditionDetailsFormValues,
) =>
  (Object.keys(values) as (keyof OrderConditionDetailsFormValues)[]).filter(
    (field) => getComparableValue(values[field]) !== getComparableValue(savedValues[field]),
  );

const parseOrderConditionDate = (date?: Date | null) => {
  const dateOnly = formatDateFromApi(date);

  return dateOnly ? parseISO(dateOnly) : null;
};

const buildFreightDefaultValues = (condition: OrderCondition) => ({
  shippingCostValue: condition.shippingCostValue ?? null,
  shippingCompanyId: condition.shippingCompany?.id ?? null,
  shippingCompanyName: condition.shippingCompany?.commercialName ?? null,
  shippingCompanyExternalId: null,
  shippingMethod: condition.shippingMethod ?? null,
  estimatedShippingBusinessDays: condition.estimatedShippingBusinessDays ?? null,
  estimatedShippingDate: parseOrderConditionDate(condition.estimatedShippingDate),
  quoteId: condition.quoteId ?? null,
  freightOrigin: condition.freightOrigin ?? null,
});

const buildDefaultValues = (condition: OrderCondition): OrderConditionDetailsFormValues => ({
  paymentId: condition.payment?.id ?? null,
  commercialPolicyId: condition.commercialPolicy?.id ?? null,
  voucherId: condition.businessPolicies.at(0)?.id ?? null,
  deliveryAddressId: condition.deliveryAddress?.id ?? null,
  billingForecastDate: parseOrderConditionDate(condition.billingForecastDate),
  ...buildFreightDefaultValues(condition),
  internalObservation: condition.internalObservation ?? null,
  observation: condition.observation ?? null,
});

const FREIGHT_FIELDS = [
  'shippingCostValue',
  'shippingMethod',
  'estimatedShippingBusinessDays',
  'estimatedShippingDate',
  'quoteId',
  'freightOrigin',
] as const;

interface BuildFreightPayloadParams {
  values: OrderConditionDetailsFormValues;
  savedValues: OrderConditionDetailsFormValues;
}

const hasQuotedShippingCompany = ({
  quoteId,
  shippingCompanyName,
  shippingCompanyExternalId,
}: OrderConditionDetailsFormValues) =>
  Boolean(quoteId && shippingCompanyName && shippingCompanyExternalId);

const hasFreightChanged = ({ values, savedValues }: BuildFreightPayloadParams) =>
  FREIGHT_FIELDS.some(
    (field) => getComparableValue(values[field]) !== getComparableValue(savedValues[field]),
  );

const buildFreightPayload = ({ values, savedValues }: BuildFreightPayloadParams) => {
  if (!hasFreightChanged({ values, savedValues })) {
    return {};
  }

  return {
    ...(hasQuotedShippingCompany(values) && {
      shippingCompany: {
        name: values.shippingCompanyName,
        externalId: values.shippingCompanyExternalId,
      },
    }),
    shippingCostValue: values.shippingCostValue,
    freightOrigin: values.freightOrigin ?? undefined,
    shippingMethod: values.shippingMethod,
    estimatedShippingBusinessDays: values.estimatedShippingBusinessDays,
    estimatedShippingDate: values.estimatedShippingDate
      ? formatDateToApi(values.estimatedShippingDate)
      : null,
    quoteId: values.quoteId,
  };
};

interface BuildShippingCompanyPayloadParams {
  values: OrderConditionDetailsFormValues;
  savedValues: OrderConditionDetailsFormValues;
}

const buildShippingCompanyPayload = ({
  values,
  savedValues,
}: BuildShippingCompanyPayloadParams) => {
  if (hasQuotedShippingCompany(values)) {
    return {};
  }

  const hasShippingCompanyChanged = values.shippingCompanyId !== savedValues.shippingCompanyId;

  if (!hasShippingCompanyChanged && !hasFreightChanged({ values, savedValues })) {
    return {};
  }

  return { shippingCompanyId: values.shippingCompanyId };
};

const useOrderConditionDetailsForm = ({
  order,
  condition,
  isOfflineMode,
}: UseOrderConditionDetailsFormParams): UseOrderConditionDetailsFormResult => {
  const queryClient = useQueryClient();
  const { translate } = useTranslation();
  const { handleErrorResponse } = useHandleAppException();
  const { team } = useApplicationContext();
  const { orderConditions, updateOrderCondition, invalidateOrderConditions } =
    useOrderConditionContext();
  const { calculateSummary } = useOrderCalculateContext();
  const { handleMessage } = snackbarStore;
  const handleLoadingStatus = useLoadingStatusStore((state) => state.handleLoadingStatus);
  const { isObservationVisible, isInternalObservationVisible } = useOrderConditionFieldsVisibility({
    order,
    condition,
    isOfflineMode,
  });
  const [pendingPropagation, setPendingPropagation] = useState<PropagableChange | null>(null);

  const notifyError = (error: Error) => {
    try {
      const message = handleErrorResponse({
        data: JSON.parse(error.message),
        ok: false,
      } as unknown as AppExceptionResponse);

      handleMessage({ message, type: 'error' });
    } catch {
      handleMessage({ message: error.message, type: 'error' });
    }
  };

  const formMethods = useForm<OrderConditionDetailsFormValues>({
    resolver: zodResolver(
      buildOrderConditionDetailsSchema({
        deliveryTermInvalidMessage: translate(
          LangEnum.ORDER_CONDITION_FREIGHT_DELIVERY_TERM_INVALID,
          { min: MIN_DELIVERY_TERM_BUSINESS_DAYS },
        ),
      }),
    ),
    defaultValues: buildDefaultValues(condition),
  });

  const savedValues = useRef(buildDefaultValues(condition));

  useEffect(() => {
    const conditionValues = buildDefaultValues(condition);

    savedValues.current = conditionValues;
    formMethods.reset(conditionValues);
  }, [condition.id]);

  useEffect(() => {
    const serverFreight = buildFreightDefaultValues(condition);
    const isSyncNeeded = shouldSyncFreightFromServer({
      serverFreight,
      savedValues: savedValues.current,
      formValues: formMethods.getValues(),
    });

    if (!isSyncNeeded) {
      return;
    }

    savedValues.current = { ...savedValues.current, ...serverFreight };

    for (const field of SERVER_FREIGHT_FIELDS) {
      formMethods.setValue(field, serverFreight[field]);
    }
  }, [condition, formMethods]);

  const mutation = useMutation({
    scope: { id: `order-condition-${condition.id}` },
    networkMode: isOfflineMode ? 'always' : undefined,
    mutationFn: async () => {
      const values = formMethods.getValues();
      const hasVoucherChanged = values.voucherId !== savedValues.current.voucherId;

      const dto = {
        id: condition.id,
        paymentId: values.paymentId,
        commercialPolicyId: values.commercialPolicyId,
        ...(hasVoucherChanged && { businessPolicyIds: values.voucherId ? [values.voucherId] : [] }),
        deliveryAddressId: values.deliveryAddressId,
        billingForecastDate: values.billingForecastDate
          ? formatDateToApi(values.billingForecastDate)
          : null,
        ...buildShippingCompanyPayload({ values, savedValues: savedValues.current }),
        ...buildFreightPayload({ values, savedValues: savedValues.current }),
        ...(isInternalObservationVisible
          ? { internalObservation: values.internalObservation }
          : {}),
        ...(isObservationVisible ? { observation: values.observation } : {}),
      } as unknown as Partial<OrderConditionSubmitData>;

      const response = await handleLoadingStatus({
        requestFn: () => orderConditionRepository(order.id, condition.id).update(dto),
      });

      return { response, submittedValues: values };
    },
    onSuccess: async ({ response, submittedValues }) => {
      savedValues.current = submittedValues;

      const updatedCondition = response?.data as GetOrderConditionDTO | undefined;

      if (updatedCondition) {
        updateOrderCondition(updatedCondition);
      } else {
        invalidateOrderConditions();
      }

      await Promise.all([
        calculateSummary(),
        queryClient.refetchQueries(validateOrderConditionsQuery(order.id, team?.id)),
      ]);
    },
    onError: (error: Error) => notifyError(error),
  });

  const propagateMutation = useMutation({
    scope: { id: `order-conditions-${order.id}` },
    mutationFn: async ({ field, value }: PropagableChange) => {
      await handleLoadingStatus({
        requestFn: () =>
          restApi.patch<void>(`orders/${order.id}/order-conditions`, { [field]: value }),
      });
    },
    onSuccess: async () => {
      invalidateOrderConditions();
      await Promise.all([
        calculateSummary(),
        queryClient.refetchQueries(validateOrderConditionsQuery(order.id, team?.id)),
      ]);
    },
    onError: notifyError,
  });

  const getPropagableChange = (
    values: OrderConditionDetailsFormValues,
    changedFields: (keyof OrderConditionDetailsFormValues)[],
  ): PropagableChange | null => {
    if (changedFields.includes('quoteId')) {
      return null;
    }

    const field = PROPAGABLE_FIELDS.find((name) => changedFields.includes(name));

    if (!field || isQuotedFreightChange(field, values)) {
      return null;
    }

    const value = values[field];
    const isClearingValue = value === null || value === undefined || value === '';

    return isClearingValue ? null : { field, value };
  };

  const save = async () => {
    const isValid = await formMethods.trigger();
    const values = formMethods.getValues();
    const changedFields = getChangedFields(values, savedValues.current);

    if (!isValid || changedFields.length === 0) {
      return;
    }

    const propagableChange =
      orderConditions.length > 1 ? getPropagableChange(values, changedFields) : null;

    mutation.mutate();

    setPendingPropagation(propagableChange);
  };

  const applyToAll = () => {
    if (pendingPropagation) {
      propagateMutation.mutate(pendingPropagation);
    }

    setPendingPropagation(null);
  };

  return {
    formMethods,
    save,
    applyToAll: {
      isOpen: Boolean(pendingPropagation),
      isSaving: propagateMutation.isPending,
      onApplyToCurrent: () => setPendingPropagation(null),
      onApplyToAll: applyToAll,
    },
  };
};

export default useOrderConditionDetailsForm;
